import { Injectable } from '@angular/core';
import { LoadingController, ToastController } from '@ionic/angular';
import { CookieService } from 'ngx-cookie-service';
import { ApiService } from './api.service';

declare var bootstrap: any;

@Injectable({
  providedIn: 'root'
})
export class GlobalService {

  loading = null;
  user: any = null;
  total_seconds = 0;
  interval = null;

  // Loaded from assets/version.json at startup (see LoadVersion) rather than hardcoded here, so
  // bumping that one file is enough to both display the version and detect a new deploy - see
  // new_version_available below.
  version_number = '';

  // True once a poll of assets/version.json (cache-busted, so it always reflects what's actually
  // deployed) finds a version different from the one this tab loaded at startup - the navbar shows
  // a "new version available" banner with a reload button when this flips, rather than reloading
  // out from under the user and risking losing whatever form they've got open.
  new_version_available = false;
  private startup_version = '';
  private version_check_interval: any = null;
  private readonly VERSION_CHECK_INTERVAL_MS = 10 * 60 * 1000;
  // Callers that need to check the version on demand (see IsRunningOutdatedVersion) await this
  // rather than reading startup_version directly, since LoadVersion's initial fetch is still async.
  private versionLoadPromise: Promise<void>;

  constructor(private toastController: ToastController, private loadingController: LoadingController, private cookieService: CookieService) {
    this.versionLoadPromise = this.LoadVersion();
  }

  // Fetches assets/version.json with a cache-busting query param so this always reads the version
  // actually deployed right now, not whatever the browser cached alongside the rest of the app shell.
  private async FetchDeployedVersion(): Promise<{ version: string; force: boolean }> {
    try {
      const response = await fetch(`assets/version.json?_=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) return { version: '', force: false };
      const data = await response.json();
      return { version: data?.version || '', force: data?.force === 1 || data?.force === true };
    } catch {
      return { version: '', force: false };
    }
  }

  async LoadVersion() {
    const { version } = await this.FetchDeployedVersion();
    if (!version) return;
    this.version_number = version;
    this.startup_version = version;
    this.StartVersionCheckTimer();
  }

  StartVersionCheckTimer() {
    clearInterval(this.version_check_interval);
    this.version_check_interval = setInterval(() => this.CheckForNewVersion(), this.VERSION_CHECK_INTERVAL_MS);
  }

  // A plain version mismatch just surfaces the "update available" banner - the user reloads on
  // their own time. If the newly deployed version.json has force = 1 though, that's a signal this
  // deploy can't be left running stale (e.g. a breaking API change), so we skip the banner and
  // reload immediately instead of waiting on the user to click it.
  async CheckForNewVersion() {
    if (this.new_version_available) return;
    const { version: latest_version, force } = await this.FetchDeployedVersion();
    if (latest_version && latest_version !== this.startup_version) {
      clearInterval(this.version_check_interval);
      if (force) {
        this.ReloadForNewVersion();
        return;
      }
      this.new_version_available = true;
    }
  }

  ReloadForNewVersion() {
    window.location.reload();
  }

  // On-demand check for pages that can't wait on the hourly-ish poll (e.g. a public email link
  // that sat unopened for days before the client finally acted on it, missing whatever features
  // shipped in between). Compares a fresh fetch against startup_version, same as CheckForNewVersion,
  // but returns immediately so the caller can force a reload rather than showing a dismissable banner.
  async IsRunningOutdatedVersion(): Promise<boolean> {
    await this.versionLoadPromise;
    if (!this.startup_version) return false;
    const { version: latest_version } = await this.FetchDeployedVersion();
    return !!latest_version && latest_version !== this.startup_version;
  }




  // The API returns dates without a trailing 'Z' (e.g. "2026-08-07T15:20:24.693"), which the Date
  // constructor and Angular's date pipe both interpret as local time instead of UTC, so times show
  // up shifted by the viewer's UTC offset. Appending 'Z' fixes that at the source.
  DateToUTCFormat = (dateTimeString: string | Date | null | undefined): string | null | undefined => {
    if (!dateTimeString) return dateTimeString as null | undefined;
    if (dateTimeString instanceof Date) return dateTimeString.toISOString();
    return dateTimeString + "Z";
  };

  //Returns the current users information that is saved in cookies
  async GetUser() {
    
    
    let user = await JSON.parse(this.cookieService.get("user"));
    if (user.is_admin == 0) user.is_admin = false;
    else user.is_admin = true;
    return user;
  }


  async UpdateUser(user) {
    this.user = user;
    this.cookieService.set('user', JSON.stringify(user));
    var return_user = JSON.parse(this.cookieService.get('user'));
    console.log(return_user);
    return this.cookieService.get('user');
  }

  async presentToastMessage(message, success) {
    let color = 'danger';
    if (success) color = 'success';
    const toast = await this.toastController.create({
      message: message,
      position: 'top',
      duration: 3000,
      color: color,
    })
    await toast.present()
  }

  validateEmail(email) {
    const regularExpression = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regularExpression.test(String(email).toLowerCase());
  }

  createGUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      var r = Math.random() * 16 | 0,
        v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  isGUID(uuid) {
    const s = "" + uuid;
    const regularExpression = s.match('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');
    if (regularExpression === null) {
      return false;
    }
    return true;
  }

  //File helpers
  GetPathName(value) {
    if(value == null || value == "") return "";
    return value.replace(/^.*(\\|\/|\:)/, '');
  }
  GetPathNameTwo(obj) {
    if(obj == null || obj == "") return "";
    if(obj.folder_title == null) return "";
    if (obj.folder_title) return obj.folder_title;
    else return this.GetPathName(obj.path);
  }
  GetFileType(value) {
    var patt1 = /\.([0-9a-z]+)(?:[\?#]|$)/i;
    var m1 = (value).match(patt1)[1];
    return m1;
  }

  //Loading Controller Present
  async presentLoading(message, duration) {
        if (this.loading == null) {

            this.loading = await this.loadingController.create({
                cssClass: 'my-custom-class',
                message: message,
                duration: duration
            });
            await this.loading.present();

            // const { role, data } = await this.loading.onDidDismiss()
        }
        else {
            this.loading.message = message;
        }
    }

  async dismissLoading() {
    console.log("Dismiss Loading");
    if (this.loading != null) {
      this.loading.dismiss();
      this.loading = null;
    }
  }


  //Timer Logic
  async CheckTimer() {
    let cookie_timer = this.cookieService.get('timer');
    let timer_running = this.cookieService.get('timer_running');
    if (cookie_timer) {
      this.total_seconds = parseInt(cookie_timer);
      if (timer_running)
        this.StartTimer();
    }
  }


  convertMsToTime(milliseconds) {
    let seconds = Math.floor(milliseconds / 1000);
    let minutes = Math.floor(seconds / 60);
    let hours = Math.floor(minutes / 60);

    seconds = seconds % 60;
    minutes = minutes % 60;

    // 👇️ If you don't want to roll hours over, e.g. 24 to 00
    // 👇️ comment (or remove) the line below
    // commenting next line gets you `24:00:00` instead of `00:00:00`
    // or `36:15:31` instead of `12:15:31`, etc.
    hours = hours % 24;

    return `${this.padTo2Digits(hours)}:${this.padTo2Digits(minutes)}:${this.padTo2Digits(
      seconds,
    )}`;
  }
  padTo2Digits(num) {
    return num.toString().padStart(2, '0');
  }

  async ResetTimer() {
    this.StopTimer();
    this.cookieService.set('timer', '', 0);
    this.total_seconds = 0;
  }

  async StartTimer() {
    this.cookieService.set('timer_running', '1', 0);
    if (!this.interval) {
      this.interval = setInterval(() => {
        //If we stop the cookie, it will stop all the timers as they hit the spot
        if (this.cookieService.get('timer_running')) {
          this.total_seconds += 1000;
          this.cookieService.set('timer', this.total_seconds.toString(), 0);
        } else {
          this.StopTimer()
        }

      }, 1000);
    }
  }

  async StopTimer() {
    clearInterval(this.interval);
    this.cookieService.set('timer_running', '', 0);
    this.interval = null;
  }

  RemoveFromArray(arr, guid) {
    var index = arr.findIndex(item => item.guid === guid);
    arr.splice(index, 1);
  }

  GetArrayIndex(arr, guid) {
    return arr.findIndex(item => item.guid === guid);
  }


  EnumToArrayTwo(enumme) {

    let map: { value: number; name: string }[] = [];
    for (var n in enumme) {
      if (typeof enumme[n] === 'number') {
        map.push({ value: <any>enumme[n], name: n });
      }
    }
    return map;
  }





  EnumToArray(enumme) {
    return Object.keys(enumme)
      .filter(value => isNaN(Number(value)) === false)
      .map(key => enumme[key]);
  }
  GetURLParameter(name: string): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
}

GetCurrentUser() {
    if (this.user) return this.user;
    let user_str = this.cookieService.get("user");
    console.log("GetCurrentUser: ", user_str);
    if (user_str == null || user_str == "") return null;
    let user = JSON.parse(user_str);
    if (user.is_admin == 0) user.is_admin = false;
    else user.is_admin = true;
    console.log("Current User: ", JSON.stringify(user));
    this.user = user;
    return user;
  }

  GetCompanyName(companies,guid) {
    let company = companies.find(c => c.guid === guid);
    return company ? company.name : null;
  }


  private currentTooltip: any = null;
  

  ShowTooltip(event: Event, message: string): void {

    let el = null;
    const target = event.target;
    if (target && target instanceof HTMLElement) {
      el = target;
    }
    if(!el) return;


    this.HideTooltip(); // Clean up any previous tooltip

    if (message == null || message == "") return;

    el.setAttribute('data-bs-toggle', 'tooltip');
    el.setAttribute('data-bs-placement', 'top');
    el.setAttribute('title', message);
    el.setAttribute('data-bs-html', 'true');

    this.currentTooltip = new bootstrap.Tooltip(el, {
      html: true
    });

    this.currentTooltip.show();
  }

  ShowTooltipConditional(event: Event, message: string, condition: boolean): void {
    if (!condition) return;

    let el = null;
    const target = event.target;
    if (target && target instanceof HTMLElement) {
      el = target;
    }
    if(!el) return;

    this.HideTooltip(); // Clean up any previous tooltip

    if (message == null || message == "") return;

    el.setAttribute('data-bs-toggle', 'tooltip');
    el.setAttribute('data-bs-placement', 'top');
    el.setAttribute('title', message);
    el.setAttribute('data-bs-html', 'true');

    this.currentTooltip = new bootstrap.Tooltip(el, {
      html: true
    });

    this.currentTooltip.show();
  }

  HideTooltip(): void {
    if (this.currentTooltip) {
      try {
        const tipElement = this.currentTooltip.getTipElement?.();
        this.currentTooltip.dispose();

        if (tipElement && tipElement.parentNode) {
          tipElement.parentNode.removeChild(tipElement);
        }
      } catch (err) {
        console.warn('Tooltip disposal failed:', err);
      }

      this.currentTooltip = null;
    }
  }

  async waitForSeconds(seconds): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, seconds * 1000));
  }



}
